/**
 * The shared table. SPEC.md §9, §45.
 *
 * Positions come from the shared ringLayout, which rotates the ring so the viewer
 * sits at the bottom of their own phone while canonical clockwise order survives
 * on every device. No trigonometry lives here. §9.1, §9.2
 *
 * Dragging is long-press activated (~250 ms) so a stray touch never turns into a
 * drag, and release swaps with the seat nearest the finger — the nearest seat
 * that is not the one being held, so the gesture answers to a short drag rather
 * than only to one that crosses half the ring. Nothing moves locally: the seat
 * move is emitted and the server's broadcast is what actually reseats the
 * ring. §9.3, §9.4
 *
 * This lives in the players overlay rather than on the score screen. Seating is
 * something a table sorts out once, when everyone sits down — the screen a phone
 * is propped up on all night is the keypad. §7.1, §18.2
 *
 * Seat dragging is pointer-only on purpose: a KeyboardSensor would swallow keys
 * the rest of the app is listening for, and the host menu's "Rearrange players"
 * is the reachable twin of this gesture. §19, §9.3
 */

import { useCallback, useMemo, useRef, type CSSProperties } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type Announcements,
  type CollisionDetection,
  type DragEndEvent,
  type ScreenReaderInstructions,
  type UniqueIdentifier,
} from '@dnd-kit/core';
import {
  avatarScaleForCount,
  ringLayout,
  truncateName,
  type PlayerView,
  type SeatSlot,
} from '@card-counter/shared';
import { haptic } from '@/lib/haptics';
import { useSession } from '@/hooks/useSession';
import { useIsHost, useMe, useMyPlayerId, usePlayersBySeat } from '@/stores/session-store';
import { PlayerSeat, suppressSeatTap } from './PlayerSeat';

/** §9.3 — inside the 200–300 ms band: long enough that a tap is never a drag. */
const LONG_PRESS_MS = 250;
/** Finger drift allowed during the long press before it is treated as a scroll. */
const LONG_PRESS_TOLERANCE_PX = 8;

/**
 * How far the finger must actually travel for a release to mean anything. §9.3
 *
 * Collision detection below deliberately never answers "your own seat", so
 * something is always the nearest *other* seat — including when the finger has
 * not moved at all. Without this, a long press that the player thought better of
 * would swap them with a neighbour on release. Comfortably larger than the
 * long-press tolerance, so the two cannot disagree about the same gesture.
 */
const MIN_DRAG_PX = 24;

/**
 * Ring radius as a share of the seat layer. The seat layer is already inset by half
 * a seat card (see `seat-ring` in index.css), so a seat can sit almost on its edge
 * without leaving the table area — and pushing the seats out is what leaves the
 * middle free for the round status on a short screen. §9.1, §7.3, §18.2
 */
const RING_RADIUS_PCT = 46;

const SEAT_PREFIX = 'seat-';

const screenReaderInstructions: ScreenReaderInstructions = {
  draggable:
    'Press and hold a seat, then drag it onto another seat to swap places. Release to drop.',
};

function seatIndexFromData(data: Record<string, unknown> | undefined): number | null {
  const value = data?.seatIndex;
  return typeof value === 'number' ? value : null;
}

/**
 * The nearest seat that is not the one being dragged. §9.3, §9.4
 *
 * Every seat is its own drop target as well as a drag source, and plain
 * `closestCenter` will happily answer with the dragged seat itself — which it
 * does for the whole first half of every drag, because that is where the finger
 * started. The result was a gesture that appeared to do nothing unless you
 * dragged well past the halfway point, which reads as broken rather than as
 * strict. Dropping the active seat from the candidates makes the whole ring
 * forgiving; `MIN_DRAG_PX` is what stops that forgiveness turning a cancelled
 * press into a swap.
 */
const nearestOtherSeat: CollisionDetection = (args) =>
  closestCenter({
    ...args,
    droppableContainers: args.droppableContainers.filter(
      (container) => seatIndexFromData(container.data.current) !== args.active.data.current?.seatIndex,
    ),
  });

export function TableRing() {
  const players = usePlayersBySeat();
  const me = useMe();
  const myPlayerId = useMyPlayerId();
  const isHost = useIsHost();
  const { moveSeat } = useSession();

  // Announcements need names, and the callbacks must not be rebuilt on every
  // score change, so they read the current table through a ref.
  const playersRef = useRef(players);
  playersRef.current = players;

  const slots = useMemo(
    () =>
      ringLayout({
        seatIndexes: players.map((player) => player.seatIndex),
        viewerSeatIndex: me?.seatIndex ?? null,
        radiusPct: RING_RADIUS_PCT,
      }),
    [players, me?.seatIndex],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { delay: LONG_PRESS_MS, tolerance: LONG_PRESS_TOLERANCE_PX },
    }),
  );

  const nameOf = useCallback((id: UniqueIdentifier | null | undefined): string => {
    if (id === null || id === undefined) return 'a player';
    const raw = String(id);
    const playerId = raw.startsWith(SEAT_PREFIX)
      ? (playersRef.current.find((p) => `${SEAT_PREFIX}${String(p.seatIndex)}` === raw)?.id ?? raw)
      : raw;
    const player = playersRef.current.find((p) => p.id === playerId);
    return player ? truncateName(player.name) : 'a player';
  }, []);

  const announcements = useMemo<Announcements>(
    () => ({
      onDragStart: ({ active }) => `Picked up ${nameOf(active.id)}.`,
      onDragOver: ({ active, over }) =>
        over ? `${nameOf(active.id)} will swap with ${nameOf(over.id)}.` : undefined,
      onDragEnd: ({ active, over }) =>
        over
          ? `${nameOf(active.id)} swapped with ${nameOf(over.id)}.`
          : `${nameOf(active.id)} stayed put.`,
      onDragCancel: ({ active }) => `${nameOf(active.id)} stayed put.`,
    }),
    [nameOf],
  );

  const onDragStart = useCallback(() => {
    suppressSeatTap();
    haptic('pickup');
  }, []);

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      // The release also produces a click in several browsers; that click must not
      // open the host's player sheet. §9.3
      suppressSeatTap();

      // A press the player thought better of. Nothing moved, so nothing moves.
      if (Math.hypot(event.delta.x, event.delta.y) < MIN_DRAG_PX) return;

      const toSeatIndex = seatIndexFromData(event.over?.data.current);
      if (toSeatIndex === null) return;

      const playerId = String(event.active.id);
      const player = playersRef.current.find((p) => p.id === playerId);
      if (!player || player.seatIndex === toSeatIndex) return;

      haptic('commit');
      // Intent only: the server swaps and broadcasts the authoritative order. §9.4
      void moveSeat(toSeatIndex, playerId === myPlayerId ? undefined : playerId);
    },
    [moveSeat, myPlayerId],
  );

  const onDragCancel = useCallback(() => {
    suppressSeatTap();
  }, []);

  const slotBySeat = new Map(slots.map((slot) => [slot.seatIndex, slot]));
  // Avatars shrink rather than overlap on a crowded table. §45
  const scale = avatarScaleForCount(players.length);
  // Published so the seat-ring inset knows how big a seat card is. §18.2
  const ringStyle = { '--seat-scale': String(scale) } as CSSProperties;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={nearestOtherSeat}
      // The gameplay screen never scrolls, so there is nothing to auto-scroll. §18.2
      autoScroll={false}
      accessibility={{ announcements, screenReaderInstructions }}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}
    >
      <div style={ringStyle} className="relative h-full w-full touch-manipulation select-none">
        {/*
          One box, holding the table and everybody sitting at it: the seats are
          placed against it and the felt is inset from it by half a seat card, so
          the two cannot drift apart when the tokens either is built from change.
          §9.2, §18.3
        */}
        <div className="seat-ring absolute">
          {/*
            The table itself, inside the ring rather than under it, so no rim
            crosses an avatar. Decorative: every player is announced through
            their own seat. §8.1
          */}
          <div aria-hidden="true" className="felt absolute" />

          <ul aria-label="Players at the table" className="absolute inset-0 m-0 list-none p-0">
            {players.map((player) => {
              const slot = slotBySeat.get(player.seatIndex);
              if (!slot) return null;
              return (
                <RingSeat
                  key={player.id}
                  player={player}
                  slot={slot}
                  scale={scale}
                  // A player may move their own avatar; the host may move any. §9.3
                  canDrag={isHost || player.id === myPlayerId}
                />
              );
            })}
          </ul>
        </div>
      </div>
    </DndContext>
  );
}

interface RingSeatProps {
  player: PlayerView;
  slot: SeatSlot;
  scale: number;
  canDrag: boolean;
}

function RingSeat({ player, slot, scale, canDrag }: RingSeatProps) {
  // Both halves carry the seat, so collision detection can tell "the seat under
  // the finger" from "the seat the finger picked up". §9.3
  const draggable = useDraggable({
    id: player.id,
    disabled: !canDrag,
    data: { seatIndex: player.seatIndex },
  });
  const droppable = useDroppable({
    id: `${SEAT_PREFIX}${String(player.seatIndex)}`,
    data: { seatIndex: player.seatIndex },
  });

  const setRefs = useCallback(
    (element: HTMLElement | null) => {
      draggable.setNodeRef(element);
      droppable.setNodeRef(element);
    },
    [draggable.setNodeRef, droppable.setNodeRef],
  );

  const { transform, isDragging } = draggable;
  const centre = 'translate(-50%, -50%)';

  const style: CSSProperties = {
    left: `${String(slot.xPct)}%`,
    top: `${String(slot.yPct)}%`,
    // Composed with the centring translate so the seat point stays the anchor.
    transform: transform
      ? `${centre} translate3d(${String(Math.round(transform.x))}px, ${String(Math.round(transform.y))}px, 0)`
      : centre,
    zIndex: isDragging ? 20 : undefined,
  };

  return (
    <li
      ref={setRefs}
      style={style}
      // left/top animate on a seat swap; the drag itself is a transform, so it
      // still tracks the finger with no lag. §9.3, §20.4
      className={[
        'absolute transition-[left,top] duration-200 ease-out-soft',
        isDragging ? 'transition-none' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <PlayerSeat
        player={player}
        scale={scale}
        dragging={isDragging}
        dropTarget={droppable.isOver && !isDragging}
        dragHandle={
          canDrag
            ? {
                ref: draggable.setActivatorNodeRef,
                listeners: draggable.listeners,
                attributes: draggable.attributes,
              }
            : undefined
        }
      />
    </li>
  );
}
